/**
 * AuthCallback — the OIDC redirect target /auth/callback (Story ADMIN-W4 AC 4).
 *
 * Reads code/state from the search params, validates the round-trip state,
 * exchanges the code (PKCE verifier from the flow state), stores the broker
 * session and navigates to the originally requested route. Any failure shows a
 * uniform error + a link to /login — never token material, never the reason.
 *
 * [Source: docs/stories/admin-w4-broker-auth.md Task 7]
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import { handleCallback } from './useBrokerAuth';

export function AuthCallback({ code, state }: { code?: string; state?: string }) {
  const router = useRouter();
  const [failed, setFailed] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard (the code is single-use)
    ran.current = true;
    if (!code || !state) {
      setFailed(true);
      return;
    }
    handleCallback(code, state)
      .then((redirectPath) => {
        void router.navigate({ to: redirectPath });
      })
      .catch(() => setFailed(true));
  }, [code, state, router]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
        {failed ? (
          <>
            <h1 className="text-base font-semibold">Anmeldung fehlgeschlagen</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Die Anmeldung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.
            </p>
            <Link
              to="/login"
              className="mt-4 inline-block w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Zur Anmeldung
            </Link>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Anmeldung wird abgeschlossen…</p>
        )}
      </div>
    </div>
  );
}
